import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  DOCUMENT_EXTENSION_ARR,
  IMG_EXTENSION_ARR,
  checkIfFileTypeIsValidForAllAttachment,
  checkIfFileTypeIsValidForImageAttachment,
  checkIfFileTypeIsValidForPdfAttachment,
  dateObjToDispViewDate,
  downloadUrl,
  formulateAttachmentPreview,
  getPhoneInputFilterOptions,
  readImageFileAsync,
  sanitizeDataTypeClientSide,
} from "../../../../utils/utils";
import {
  Form,
  Input,
  Typography,
  Button,
  Select,
  DatePicker,
  Flex,
  Row,
  Col,
  Modal,
  Tooltip,
  Upload,
} from "antd";
import {
  fileDocumentUploadAction,
  fileImageUpload,
  fileUploadAction,
  getCountryAction,
  getUserProfileAction,
  updateStaffProfileAction,
} from "../../../../redux/services/auth/authServices";
import RequiredLabel from "../../../common/required-label/RequiredLabel";
import dayjs from "dayjs";
import { useRef } from "react";
import { FileAttachment } from "../../../common/fileAttachment";
import { CameraIcon, InfoButtonIcon } from "../../../../assets/svg";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../constants/config/appConfig";
import logo from "../../../../assets/images/logo.png";
import styles from "./ProfileSettings.module.scss";
import LanguageDropdown from "../../../common/language-dropdown/LanguageDropdown";
import { LeftOutlined } from "@ant-design/icons";
import { colors } from "../../../../constants/colors";
import { baseRouteMaker, routes } from "../../../../constants/routeConsts";
import CountryPhoneInput from "antd-country-phone-input";
import { allowCharactersOnlyForIntegerNumbers } from "../../../../utils/appKeyStrokeLimitingUtils";
import { updateStaffAction } from "../../../../redux/services/staff/staffServices";
import Spinner from "../../../common/spinner/Spinner";
import { CameraOutlined } from "@ant-design/icons";
import { userRoles } from "../../../../constants";
import { PaperClipOutlined } from "@ant-design/icons";
import Actions from "../../../../redux/actions";
import { getCategoriesSelectListAction } from "../../../../redux/services/categories/categoriesServices";
import { LanguageContext } from "../../../../context/LanguageProvider";
import { toastService } from "../../../common/toast-messages/toast";

const { Title } = Typography;

const ProfileSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const category = Form.useWatch("category", { form, preserve: true });

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [imageLoader, setImageLoader] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* module dependencies for attachment - single */
  const [singleAttachmentObj, setSingleAttachmentObj] = useState(null);
  const [
    singleAttachmentSelectedImageUrl,
    setSingleAttachmentSelectedImageUrl,
  ] = useState("");
  const attachmentRef = useRef(null);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  /* module dependencies for attachment - single */

  /* module dependencies for attachment - multiple */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [multipleAttachmentList, setMultipleAttachmentList] = useState([]);
  /* module dependencies for attachment - multiple */

  /* local storage dependencies */
  const { useSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  const { selectedLanguage } = useContext(LanguageContext);
  /* local storage dependencies */

  /* select list dependencies */
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { categoriesSelectList, subCategoriesSelectList } = useSelector(
    (state) => state.categories
  );
  /* select list dependencies */

  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  /* on Image changed- single attachment */

  const onImageChanged = (e) => {
    const file = e.target.files[0];

    const attachmentFileName = file.name;

    const fileTypeIsValidRes =
      checkIfFileTypeIsValidForImageAttachment(attachmentFileName);

    if (fileTypeIsValidRes.isValid === false) {
      toastService.error(fileTypeIsValidRes.isInvalidMessage);
    } else {
      setSingleAttachmentObj(file);
      if (file) {
        const formData = new FormData();
        formData.append("image", file);
        let formDataBody = formData;
        setImageLoader(true);
        dispatch(fileImageUpload(formDataBody)).then((apiRes) => {
          const imageUrl = apiRes?.[0];
          setSingleAttachmentSelectedImageUrl(imageUrl);
          setImageLoader(false);
        });
      }
    }
  };
  /* on Image changed- single attachment */

  /* on Category changed */
  const onCategoryChanged = (id, value) => {
    form.resetFields(["subCategories"]);
    if (value && value !== null && value !== undefined) {
      setSelectedCategory(value);
      dispatch(
        Actions.createAction(Actions.GET_SUB_CATEGORIES_SELECT_LIST, {
          subCategoriesSelectList: value?.subCategories,
        })
      );
    }
  };
  /* on Category changed */

  /* open image preview for dialog - multiple attachment */
  const handleAttachmentPreview = async (file) => {
    const formulateAttachment = await formulateAttachmentPreview(
      file,
      file?.name,
      file.url
    );
    if (!formulateAttachment?.isFileTypeImage) {
      downloadUrl(formulateAttachment?.fileImagePreview);
    } else {
      if (!file.url && !file.preview) {
        file.preview = await readImageFileAsync(file.originFileObj);
      }
      setPreviewImageSrc(file?.url || file?.preview);
      setOpenPreviewDialog(true);
      const tempPreviewTitle =
        file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
      setPreviewTitle(tempPreviewTitle);
    }
  };
  /* open image preview for dialog - multiple attachment */

  /* function definition for select list API calls */
  const selectListApiCalls = (details) => {
    // country select list
    dispatch(getCountryAction());

    // Categories select list
    dispatch(
      getCategoriesSelectListAction(setSelectListLoader, selectedLanguage)
    ).then((apiRes) => {
      const categories = apiRes;
      details &&
        categories &&
        categories?.forEach((element) => {
          if (element._id === details?.categoryInfo?.category?._id) {
            dispatch(
              Actions.createAction(Actions.GET_SUB_CATEGORIES_SELECT_LIST, {
                subCategoriesSelectList: element?.subcategories,
              })
            );
          }
        });
    });
  };
  /* function definition for select list API calls */

  // country api call
  useEffect(() => {
    selectListApiCalls();
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(getUserProfileAction(setUserSession)).then((apiRes) => {
      const userDetails = apiRes;
      setSelectedUserDetails(userDetails);
      selectListApiCalls(userDetails);

      const categoryName = userDetails?.categoryInfo?.category?.name;

      setSelectedCategory({
        label: categoryName,
        value: userDetails?.categoryInfo?.category._id,
      });
      if (userDetails) {
        form.setFieldsValue({
          firstName: userDetails?.first_name,
          lastName: userDetails?.last_name,
          email: userDetails?.email,
          dateOfBirth: dayjs(userDetails?.date_of_birth, "YYYY/MM/DD") || null,
          gender: userDetails?.gender
            ? { value: userDetails?.gender, label: userDetails?.gender }
            : null,
          phone_number: {
            phone: userDetails?.phone?.phone_number,
            code: Number(userDetails?.phone?.country_code),
          },
          category: categoryName,
          subCategories: userDetails?.categoryInfo?.subcategories_full?.map(
            (e) => e._id
          ),
        });
        setSingleAttachmentSelectedImageUrl(userDetails?.profile_image);
        setMultipleAttachmentList(
          userDetails?.documents?.map((element) => ({
            ...element,
            url: element?.documentUrl,
          }))
        );
        setSelectedStaffId(userDetails?._id);
        setLoading(false);
      }
    });
    setLoading(false);
  }, []);

  const onFinish = (values) => {
    let dataToBeSent = {
      first_name: values?.firstName,
      last_name: values?.lastName,
      email: values?.email,
      gender: values?.gender ? values?.gender?.value : "",
      date_of_birth:
        dateObjToDispViewDate(values?.dateOfBirth, "yyyymmdd") || "",
      profile_image: singleAttachmentSelectedImageUrl ?? "",
      phone: {
        phone_number: values?.phone_number?.phone,
        country_code: values?.phone_number?.code,
      },
      fees: selectedUserDetails?.fees,
      experience: selectedUserDetails?.experience,
      availability: selectedUserDetails?.availability?.map(
        ({ _id, ...element }) => ({ ...element })
      ),
      categoryInfo: {
        categoryName: selectedCategory?.label,
        category: selectedCategory?.value,
        subcategories: values?.subCategories,
      },
      documents: multipleAttachmentList?.map((element) => ({
        name: element?.name,
        documentUrl: element?.url,
      })),
      _id: selectedStaffId,
    };
    dispatch(
      updateStaffAction(dataToBeSent, navigate, setLoading, setUserSession)
    );
  };

  const disabledDate = (value) => {
    let eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(new Date().getFullYear() - 18);
    return value > eighteenYearsAgo;
  };

  return (
    <>
      <Flex vertical gap="2rem" className={styles.profileSettingsContainer}>
        <div className={styles.logoContainer}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Button
                type="default"
                size="small"
                icon={<LeftOutlined />}
                onClick={() => navigate(baseRouteMaker(routes.STAFF_DASHBOARD))}
              >
                {t("CommonUtils.back")}
              </Button>
            </Col>
            <Col span={8}>
              <img src={logo} alt="ReynaCare" />
            </Col>
            <Col span={8} style={{ textAlign: "end" }}>
              <LanguageDropdown width="10px" height="10px" />
            </Col>
          </Row>
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Row gutter={24} justify="space-between">
              <Col span={10}>
                <Flex
                  vertical
                  gap="1rem"
                  className={styles.profileSetupContainer}
                >
                  <Title level={1} style={{ color: colors.primaryColor }}>
                    {t("ProfileSetup.editYourProfile")}
                  </Title>
                  <Title level={4} type="secondary" style={{ margin: 0 }}>
                    {t("ProfileSetup.subTitle")}
                  </Title>
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    autoComplete="off"
                    requiredMark={false}
                    className={styles.profileSettingsForm}
                  >
                    <div>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.firstName")}
                            required={true}
                          />
                        }
                        name="firstName"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterFirstName"
                            ),
                          },
                          {
                            whitespace: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterFirstName"
                            ),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "Modules.Staff.StaffForm.enterFirstName"
                          )}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.lastName")}
                            required={true}
                          />
                        }
                        name="lastName"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterLastName"
                            ),
                          },
                          {
                            whitespace: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterLastName"
                            ),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "Modules.Staff.StaffForm.enterLastName"
                          )}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.emailAddress")}
                            required={true}
                          />
                        }
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterEmailAddress"
                            ),
                          },
                          {
                            whitespace: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseEnterEmailAddress"
                            ),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "Modules.Staff.StaffForm.enterEmailAddress"
                          )}
                        />
                      </Form.Item>

                      <Form.Item
                        initialValue={{
                          code: 971,
                        }}
                        label={
                          <RequiredLabel
                            text={t("SignUp.phoneNumberLabel")}
                            required={true}
                          />
                        }
                        name="phone_number"
                        rules={[
                          {
                            required: true,
                            validator: async (_, value) => {
                              if (value && !value.phone)
                                return Promise.reject(
                                  t("SignUp.phoneNumberErrorMessage")
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <CountryPhoneInput
                          selectProps={{
                            filterOption: (input, option) =>
                              getPhoneInputFilterOptions(input, option),
                          }}
                          maxLength={15}
                          onKeyDown={(e) =>
                            allowCharactersOnlyForIntegerNumbers(e)
                          }
                          inline
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.dateOfBirth")}
                            required={true}
                          />
                        }
                        name="dateOfBirth"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseSelectDateOfBirth"
                            ),
                          },
                        ]}
                      >
                        <DatePicker
                          onKeyDown={(e) => e.preventDefault()}
                          style={{ width: "100%" }}
                          disabledDate={disabledDate}
                          placeholder={t(
                            "Modules.Staff.StaffForm.selectDateOfBirth"
                          )}
                          format={"DD-MM-YYYY"}
                        />
                      </Form.Item>

                      <Form.Item
                        name="gender"
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.gender")}
                            required={false}
                          />
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseSelectGender"
                            ),
                          },
                        ]}
                      >
                        <Select
                          options={[
                            {
                              label: "Male",
                              value: "Male",
                            },
                            {
                              label: "Female",
                              value: "Female",
                            },
                          ]}
                          placeholder={t(
                            "Modules.Staff.StaffForm.selectGender"
                          )}
                        />
                      </Form.Item>

                      {selectedUserDetails?.role &&
                      selectedUserDetails?.role ===
                        userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR ? (
                        <Form.Item>
                          <Flex vertical gap="1rem">
                            <Flex gap="0.2rem" align="center">
                              <Title level={4}>
                                {t("Modules.Staff.StaffForm.uploadDocuments")}
                              </Title>
                              <Tooltip
                                placement="topLeft"
                                title={t(
                                  "CommonUtils.multiplePdfDocumentsTooltip"
                                )}
                                color={colors.DarkMint}
                              >
                                <Button type="text" shape="circle" size="small">
                                  <Flex align="center" justify="center">
                                    <InfoButtonIcon />
                                  </Flex>
                                </Button>
                              </Tooltip>
                            </Flex>
                            <Flex vertical align="center" justify="center">
                              <Upload
                                listType="picture-card"
                                fileList={multipleAttachmentList}
                                multiple
                                onPreview={handleAttachmentPreview}
                                onChange={(options) => {
                                  const tempFileTypeIsValidResArr =
                                    options?.fileList?.map((element) =>
                                      checkIfFileTypeIsValidForAllAttachment(
                                        element.name
                                      )
                                    );

                                  const isValid =
                                    !tempFileTypeIsValidResArr.some(
                                      (element) => element.isValid === false
                                    );
                                  const fileTypeIsValidRes =
                                    tempFileTypeIsValidResArr.find(
                                      (element) => element.isValid === false
                                    );

                                  if (isValid === false) {
                                    toastService.error(
                                      fileTypeIsValidRes.isInvalidMessage
                                    );
                                  } else {
                                    setMultipleAttachmentList(options.fileList);
                                  }
                                }}
                                onRemove={(file) =>
                                  setMultipleAttachmentList(
                                    multipleAttachmentList?.filter(
                                      (element) => element.uid !== file.uid
                                    )
                                  )
                                }
                                customRequest={(options) => {
                                  const attachmentFileName = options.file.name;
                                  const fileTypeIsValidRes =
                                    checkIfFileTypeIsValidForAllAttachment(
                                      attachmentFileName
                                    );

                                  if (fileTypeIsValidRes.isValid === false) {
                                    toastService.error(
                                      fileTypeIsValidRes.isInvalidMessage
                                    );
                                  } else {
                                    const formData = new FormData();
                                    if (
                                      IMG_EXTENSION_ARR.includes(
                                        fileTypeIsValidRes.fileExtension
                                      )
                                    ) {
                                      formData.append("images", options.file);
                                    } else if (
                                      DOCUMENT_EXTENSION_ARR.includes(
                                        fileTypeIsValidRes.fileExtension
                                      )
                                    ) {
                                      formData.append(
                                        "documents",
                                        options.file
                                      );
                                    }
                                    dispatch(fileUploadAction(formData)).then(
                                      (apiRes) => {
                                        let response = apiRes;
                                        let url = "";
                                        if (apiRes?.documents?.length > 0) {
                                          response = apiRes?.documents[0];
                                          url =
                                            apiRes?.documents[0]?.documentUrl;
                                        } else if (apiRes?.images?.length > 0) {
                                          response = apiRes?.images[0];
                                          url = apiRes?.images[0]?.url;
                                        }
                                        options.file["url"] = url;
                                        options.onSuccess(
                                          response,
                                          options.file
                                        );
                                      }
                                    );
                                  }
                                }}
                                className="uploadContainer"
                              >
                                <Button
                                  type="primary"
                                  shape="circle"
                                  size="middle"
                                  icon={<PaperClipOutlined />}
                                />
                              </Upload>
                              <Modal
                                open={openPreviewDialog}
                                title={previewTitle}
                                footer={null}
                                onCancel={() => setOpenPreviewDialog(false)}
                              >
                                <img
                                  style={{ width: "100%" }}
                                  src={previewImageSrc}
                                />
                              </Modal>
                            </Flex>
                          </Flex>
                        </Form.Item>
                      ) : (
                        <></>
                      )}

                      <Form.Item
                        name="category"
                        label={
                          <RequiredLabel
                            text={t("Modules.Staff.StaffForm.category")}
                            required={true}
                          />
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Staff.StaffForm.pleaseSelectCategory"
                            ),
                          },
                        ]}
                      >
                        <Select
                          loading={selectListLoader}
                          options={categoriesSelectList?.filter(
                            (item) => item.is_active
                          )}
                          placeholder={t(
                            "Modules.Staff.StaffForm.selectCategory"
                          )}
                          onChange={onCategoryChanged}
                        />
                      </Form.Item>
                      {category &&
                      category !== null &&
                      category !== undefined ? (
                        <Form.Item
                          name="subCategories"
                          label={
                            <RequiredLabel
                              text={t("Modules.Staff.StaffForm.subCategory")}
                              required={true}
                            />
                          }
                          rules={[
                            {
                              required: true,
                              message: t(
                                "Modules.Staff.StaffForm.pleaseSelectSubCategory"
                              ),
                            },
                          ]}
                        >
                          <Select
                            mode="multiple"
                            options={subCategoriesSelectList}
                            placeholder={t(
                              "Modules.Staff.StaffForm.selectSubCategory"
                            )}
                          />
                        </Form.Item>
                      ) : (
                        <></>
                      )}
                    </div>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: "100%" }}
                    >
                      {t("ProfileSetup.updateProfile")}
                    </Button>
                  </Form>
                </Flex>
              </Col>
              <Col span={12}>
                <Flex vertical gap="2rem" align="center">
                  <Flex
                    vertical
                    align="center"
                    style={{ position: "relative" }}
                  >
                    <FileAttachment
                      attachmentObj={singleAttachmentObj}
                      attachmentUrl={singleAttachmentSelectedImageUrl}
                      loading={imageLoader}
                    />

                    <div
                      className="attachmentContainer"
                      style={{
                        cursor: "pointer",
                        position: "absolute",
                        bottom: "-25px",
                        background:colors.turquoiseGreen,
                        borderRadius:10,
                      }}
                      onClick={() => attachmentRef.current?.click()}
                    >
                      
                      <Button
                         type="primary"
                         shape="circle"
                        size="middle"
                        style={{background:colors.turquoiseGreen}}
                        icon={<CameraOutlined 
                          style={{ fontSize: 22,}} 
                         />}
                      />
                    </div>
                    <input
                      type="file"
                      style={{ display: "none" }}
                      ref={attachmentRef}
                      accept="image/*"
                      onChange={onImageChanged}
                    />
                  </Flex>
                  <Typography.Title
                    level={3}
                    type="secondary"
                    style={{ marginBottom: 10 }}
                  >
                    {t("CommonUtils.supportedImageFormats")}
                  </Typography.Title>
                </Flex>
              </Col>
            </Row>
          </>
        )}
      </Flex>
    </>
  );
};

export default ProfileSettings;
